<template>
    <div
        :class="{ [`l-ss-c-filter-enum-${filter.name}`]: true, 'l-ss-c-filter-is-short': isShort }"
        class="l-ss-c-filter-enum">
        <c-label
            v-if="showTitle"
            :label="filterLabel"
            :parse-staples="true"/>
        <span
            v-if="showSearchField && hasSearchField"
            class="l-ss-c-filter-enum-search-wrap">
            <app-input
                v-model="localSearch"
                class="l-ss-c-filter-enum-search-input"
                :placeholder="$t('filters.enumSearch')"
                :show-search-icon="true"/>
        </span>
        <div
            class="l-ss-c-filter-enum-group-wrapper"
            :class="{
                'l-ss-c-filter-enum-group-wrapper-full': showMore,
                'l-ss-c-filter-enum-group-wrapper-is-tag-checkboxes': true,
            }">
            <app-checkbox
                v-for="(option, index) in filterOptionsSort"
                :key="`filter-checkbox-${option.id}`"
                :value="isChecked(option.id)"
                class="l-ss-c-filter-enum-option"
                :class="{ 'l-ss-c-filter-enum-option-primary': isPrimaryOption(option.id) }"
                @change="onToggle($event, option, index)">
                <div class="l-ss-c-filter-enum-option-content">
                    <span
                        v-if="isColorFilter"
                        class="l-ss-c-filter-enum-option-color"
                        :class="{ 'l-ss-c-filter-enum-option-color-white': isWhiteColor(option.name) }"
                        :style="getColor(option.name)"></span>
                    <span v-html="transformValue(option.name)"></span>
                    <strong v-if="option.hits">({{ option.hits }})</strong>
                </div>
            </app-checkbox>
        </div>
        <show-more-btn
            v-if="hasShowMore"
            class="l-ss-c-navigation-list-item-action"
            @click="emitChangeSize"
            v-model="showMore"/>
    </div>
</template>

<script>
    import FilterMixin, { MIN_ITEMS_COUNT_FOR_SHOW_MORE, MIN_ITEMS_COUNT_FOR_SEARCH_FIELD } from './mixin';
    import CLabel from './components/label';
    import AppInput from '@/core/components/common/app-input.vue';
    import AppCheckbox from '@/core/components/common/app-checkbox.vue';
    import ShowMoreBtn from '@/core/components/show-more-btn.vue';

    const COLORS = {
        'Бежевый': '#c8b6a7',
        'Белый': '#ffffff',
        'Бронзовый': 'conic-gradient(#ffeed0, #bf905d, #ffeed0, #bf905d, #ffeed0)',
        'Глянцевый': 'conic-gradient(#676767, #000000;, #676767, #000000;, #676767)',
        'Голубой': '#76d7f5',
        'Бирюзовый': '#7dd7ce',
        'Желтый': '#fcff50',
        'Зеленый': '#00bb5c',
        'Хаки': '#00bb5c',
        'Золотистый': 'conic-gradient(#fefec5, #bfa75d, #fefec5, #bfa75d, #fefec5)',
        'Красный': '#d63a45',
        'Бордовый': '#9b2d30',
        'Бордо': '#651212',
        'Оранжевый': '#fcaf4c',
        'Коричневый': '#9e6d48',
        'Розовый': '#f5a7d2',
        'Светло-зеленый': '#bbf376',
        'Серебристый': 'conic-gradient(#f7f6f6, #a3a2a2, #f7f6f6, #a3a2a2, #f7f6f6)',
        'Серый': '#c4c4c4',
        'Синий': '#4177f1',
        'Сиреневый': '#a560c9',
        'Темно-серый': '#545c69',
        'Фиолетовый': '#8560c9',
        'Темно-фиолетовый': '#9400d3',
        'Черный': '#000000',
        'Прозрачный': 'conic-gradient(#ffffff, #b5b5b5, #ffffff, #b5b5b5, #ffffff)',
    };

    export default {
        components: {
            ShowMoreBtn,
            AppCheckbox,
            AppInput,
            CLabel,
        },
        mixins: [
            FilterMixin,
        ],
        props: {},
        data() {
            return {
                values: [],
                trackParams: [],
            };
        },
        computed: {
            filterOptions() {
                let options = this.filter.options;

                if (this.localSearch) {
                    const localSearch = this.localSearch.trim().toLowerCase();
                    options = options.filter(item => item.name.toLowerCase().includes(localSearch));
                }

                return options;
            },

            filterOptionsSort() {
                /**
                 * Сортирует массив фильтров в соответствии с их выборкой.
                 * @param {Array} options - массив опций фильтра
                 * @returns {Array} отсортированный массив опций фильтра
                 */
                const sortActiveOnTop = (options) => {
                    const selectedIndexMap = new Map(this.values.map((id, index) => [id, index]));

                    return options.sort((a, b) => {
                        let indexA = selectedIndexMap.get(a.id);
                        let indexB = selectedIndexMap.get(b.id);

                        if (indexA === undefined) {
                            indexA = Infinity;
                        }

                        if (indexB === undefined) {
                            indexB = Infinity;
                        }

                        return indexA - indexB;
                    });
                };

                let options = this.values.length ? sortActiveOnTop(this.filterOptions) : this.filterOptions;

                if (!this.showMore) {
                    options = options.slice(0, MIN_ITEMS_COUNT_FOR_SEARCH_FIELD);
                }

                return options;
            },

            optionsIndex() {
                return this.filter.options.slice(0).map(item => ({
                    ...item,
                    searchIndex: item.name.toLowerCase(),
                    firstLetter: item.name.charAt(0).toLowerCase(),
                }));
            },

            showSearchField() {
                return this.localSearch || this.optionsIndex.length > MIN_ITEMS_COUNT_FOR_SEARCH_FIELD;
            },

            hasShowMore() {
                if (this.forceShowAll) {
                    return false;
                }

                const options = this.localSearch ? this.filterOptionsSort : this.filter.options;

                return options.length > MIN_ITEMS_COUNT_FOR_SHOW_MORE + 1;
            },

            isColorFilter() {
                return this.filter?.id.toLowerCase().includes('цвет');
            },

            filterId() {
                return this.filter?.id || '';
            },

            filterConfig() {
                return this.$configuration.filterConfig || {};
            },

            currentFilterConfig() {
                return this.filterConfig[this.filterId] || null;
            },

            isShortCurrentFilterView() {
                if (this.currentFilterConfig?.isShort === false) {
                    return false;
                }

                if (this.currentFilterConfig?.isShort) {
                    return true;
                }

                return this.$configuration.isShortFilterView;
            },

            hasSearchField() {
                return this.currentFilterConfig?.hasSearch !== false;
            },
        },
        watch: {
            filterValue: {
                handler(value) {
                    if (value && value.options) {
                        this.values = value.options.slice(0);
                        this.values = Array.from(new Set(this.values));
                    } else {
                        this.values = [];
                    }
                },
                immediate: true,
            },
            filterOptionsSort(value, oldValue) {
                if (value.length !== oldValue.length) {
                    this.$nextTick(() => {
                        this.$emit('change-size');
                    });
                }
            },
        },
        created() {
            if (this.forceShowAll) {
                this.showMore = true;
            }
        },
        mounted() {
            const options = this.options || [];
            if (this.values.length && !this.values.every(optionId => options.find(item => item.id === optionId))) {
                this.showMore = true;
            }
        },
        methods: {
            isChecked(optionId) {
                return this.values.includes(optionId);
            },
            onToggle(eventData, option, index) {
                const checked = this.isChecked(option.id);
                const values = this.values.slice(0);
                if (checked) {
                    values.splice(values.indexOf(option.id), 1);
                    this.trackParams.splice(this.trackParams.indexOf(option.id), 1);
                } else {
                    values.push(option.id);

                    const trackValue = {
                        ...option,
                        pos: index + 1,
                    };

                    this.trackParams.push(trackValue);
                }
                if (values.length) {
                    this.onChange({
                        condition: 'in',
                        options: values.slice(0),
                    });

                    this.onFilterClick(eventData.event, this.trackParams);
                } else {
                    this.onChange();
                    this.onFilterClick(eventData.event);
                }
            },
            getColor(name) {
                const firstName = name.split('/')[0].trim();
                return { background: COLORS[firstName] };
            },
            isWhiteColor(name) {
                return this.getColor(name).background === COLORS['Белый'];
            },
            isPrimaryOption(optionId) {
                const primaryOptions = this.$configuration.primaryFilterOptions[this.filter.name];
                return primaryOptions && primaryOptions.includes(optionId);
            },
        },
    };
</script>
